import { Tag } from "antd-v5";
import React, { ReactNode } from "react";
import { AssetEsDto } from "types/assetdetails";

import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACPopover from "ac-components/ac-popover/ACPopover";

import "./CorrelationBasisPopover.less";
import {
  CORRELATED_VALUE_KEYS,
  CORRELATION_TAG_ICONS,
  CorrelatedValueKey,
  getCorrelatedValueLabel,
  getCorrelationRuleTags,
} from "./correlationUtils";

interface CorrelationBasisPopoverProps {
  rule: string | null;
  sourceMap: AssetEsDto[];
  correlatedAsset: AssetEsDto;
  children: ReactNode;
}

// Get the display name for an asset
const getAssetDisplayName = (asset: AssetEsDto): string => {
  return asset.name || `ID: ${asset.id}`;
};

// Find the primary/parent asset from sourceMap using primaryAssetId
const findPrimaryAsset = (
  sourceMap: AssetEsDto[],
  primaryAssetId: string | null | undefined,
): AssetEsDto | null => {
  if (!primaryAssetId || !sourceMap?.length) return null;
  return (
    sourceMap.find((asset) => asset.assetIdFromTool === primaryAssetId) || null
  );
};

// Get correlation value columns from correlatedValues object
const getCorrelationColumns = (
  correlatedValues:
    | Record<string, string | null | undefined>
    | null
    | undefined,
): { key: CorrelatedValueKey; label: string; value: string | null }[] => {
  if (!correlatedValues) return [];

  const columns: {
    key: CorrelatedValueKey;
    label: string;
    value: string | null;
  }[] = [];

  // Check each possible correlation value key
  CORRELATED_VALUE_KEYS.forEach((key) => {
    const value = correlatedValues[key];
    if (value !== undefined) {
      columns.push({
        key,
        label: getCorrelatedValueLabel(key),
        value: value || null,
      });
    }
  });

  return columns;
};

const CorrelationBasisPopover = ({
  rule,
  sourceMap,
  correlatedAsset,
  children,
}: CorrelationBasisPopoverProps) => {
  if (!rule) {
    return <>{children}</>;
  }

  const assetCorrelationContext = correlatedAsset.assetCorrelationContext;
  const primaryAssetId = assetCorrelationContext?.primaryAssetId;
  const primaryAsset = findPrimaryAsset(sourceMap, primaryAssetId);

  // If we can't find the primary asset, don't show the popover
  if (!primaryAsset) {
    return <>{children}</>;
  }

  const correlatedValues = assetCorrelationContext?.correlatedValues;
  const correlationColumns = getCorrelationColumns(correlatedValues);
  const tags = getCorrelationRuleTags(rule);

  return (
    <ACPopover
      trigger="hover"
      placement="top"
      content={
        <div className="correlation-basis-popover">
          {/* Header row with column names */}
          <div className="correlation-basis-popover__header d-flex text-body-sm text-secondary">
            <div className="correlation-basis-popover__name-cell">
              Asset Name
            </div>
            <div className="correlation-basis-popover__value-cell">
              Primary Asset
            </div>
            {correlationColumns.map((column) => (
              <div
                key={column.key}
                className="correlation-basis-popover__value-cell"
              >
                {column.label}
              </div>
            ))}
          </div>

          {/* Data row - shows hovered/correlated asset info */}
          <div className="correlation-basis-popover__row d-flex text-body-sm">
            <div
              className="correlation-basis-popover__name-cell text-ellipsis"
              title={getAssetDisplayName(correlatedAsset)}
            >
              {getAssetDisplayName(correlatedAsset)}
            </div>
            <div
              className="correlation-basis-popover__value-cell text-ellipsis"
              title={getAssetDisplayName(primaryAsset)}
            >
              {getAssetDisplayName(primaryAsset)}
            </div>
            {correlationColumns.map((column) => (
              <div
                key={column.key}
                className="correlation-basis-popover__value-cell text-ellipsis"
                title={column.value || "-"}
              >
                {column.value || NOT_AVAILABLE}
              </div>
            ))}
          </div>

          {/* Tags at bottom */}
          {tags && tags.length > 0 && (
            <div className="correlation-basis-popover__tags d-flex gap-1">
              {tags.map((tag, index) => (
                <Tag
                  key={index}
                  color={tag.color}
                  icon={CORRELATION_TAG_ICONS[tag.iconType]}
                >
                  {tag.label}
                </Tag>
              ))}
            </div>
          )}
        </div>
      }
    >
      {children}
    </ACPopover>
  );
};

export default CorrelationBasisPopover;
